import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'
import { GiftMessageSaveInput, Callback } from 'lib/types'
import { Slice, GiftMessageSaveSlice } from './types'
import { PWAThunk } from 'lib/core/data'

export const initialState: Slice = {}

interface ActionData {
  cartId: string
  uuid: string
}

const getSlice = (slice: GiftMessageSaveSlice, uuid?: string): GiftMessageSaveSlice => {
  return slice ?? ({
    uuid,
    isFetching: false,
    isSuccess: false,
    isFailure: false
  })
}

const slice = createSlice({
  name: 'quote/giftMessageSave',
  initialState,
  reducers: {
    request (state, action: PayloadAction<ActionData>) {
      const { cartId, uuid } = action.payload
      const slice = getSlice(state[cartId], uuid)
      slice.uuid = uuid
      slice.isFetching = true
      state[cartId] = slice
    },

    success (state, action: PayloadAction<ActionData>) {
      const { cartId, uuid } = action.payload
      const slice = getSlice(state[cartId])
      if (uuid === slice.uuid) {
        slice.isFetching = false
        slice.isSuccess = true
        slice.isFailure = false
      }
    },

    failure (state, action: PayloadAction<ActionData>) {
      const { cartId, uuid } = action.payload
      const slice = getSlice(state[cartId])
      if (uuid === slice.uuid) {
        slice.isFetching = false
        slice.isSuccess = false
        slice.isFailure = true
      }
    }
  }
})

export const reducer = slice.reducer

export const { request, success, failure } = slice.actions

export function saveGiftMessage (input: GiftMessageSaveInput, callback?: Callback): PWAThunk {
  return async (dispatch, getState, params) => {
    const { magentoClient } = params
    const { cartId } = input
    const uuid = uuidv4()
    dispatch(request({ cartId, uuid }))
    try {
      const response = await magentoClient.getClient().quote.saveGiftMessage(input)

      if (response.data?.visitureAddGiftMessage) {
        dispatch(success({ cartId, uuid }))
        callback?.onSuccess?.()
      } else {
        dispatch(failure({ cartId, uuid }))
        callback?.onFailure?.({
          error: new Error(`[Gift Message] Unexpected payload: ${JSON.stringify(response)}`),
          extraData: {
            ...input
          }
        })
      }

      return response
    } catch (e) {
      dispatch(failure({ cartId, uuid }))
      callback?.onFailure?.({
        error: new Error(`[Gift Message] ${e.message}`),
        extraData: {
          ...input
        }
      })
    }
  }
}
